import { Select } from './ui/Field.tsx';

export interface ManagerOption {
  id: string;
  name: string;
}

// Shared manager picker — used by the HR Members table (assign an
// employee's default manager) and the HR case-detail page (reassign a
// single case's manager). Both need the same fix: a native <select> whose
// bound `value` has no matching <option> silently renders the FIRST
// option as selected instead of the real value — which would show HR a
// manager that isn't actually assigned (found live: deactivating a
// manager made every one of their reports/cases appear reassigned to a
// different manager on screen, with no real change underneath).
//
// `managers` is `undefined` while the caller's list is still loading — a
// real, distinct signal from "loaded and empty" ([]). Treating the two the
// same was itself a bug: a still-loading list rendered every value as "not
// in the list", mislabelling an Active manager as inactive. Callers pass
// `currentManager` — the value they already have from their own data,
// independent of whether the picker list has loaded — and this component
// computes the "not in the (loaded) list" fallback internally, rather than
// trusting every caller to reimplement that check identically.
export function ManagerSelect({
  ariaLabel,
  value,
  managers,
  currentManager,
  disabled,
  onChange,
  className,
}: {
  ariaLabel: string;
  value: string | null;
  managers: ManagerOption[] | undefined;
  currentManager: ManagerOption | null;
  disabled: boolean;
  onChange: (managerId: string) => void;
  className?: string;
}) {
  if (managers === undefined) {
    return (
      <Select
        aria-label={ariaLabel}
        value={value ?? ''}
        disabled
        onChange={() => {}}
        className={className ?? 'py-1.5 text-sm'}
      >
        <option value={value ?? ''}>{currentManager?.name ?? 'Loading…'}</option>
      </Select>
    );
  }

  const fallback =
    value && !managers.some((mgr) => mgr.id === value) ? (currentManager ?? null) : null;

  return (
    <Select
      aria-label={ariaLabel}
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => {
        if (e.target.value) onChange(e.target.value);
      }}
      className={className ?? 'py-1.5 text-sm'}
    >
      <option value="" disabled>
        {managers.length === 0 ? 'No active managers' : 'Not assigned'}
      </option>
      {fallback ? (
        <option value={fallback.id} disabled>
          {fallback.name} (inactive — reassign)
        </option>
      ) : null}
      {managers.map((mgr) => (
        <option key={mgr.id} value={mgr.id}>
          {mgr.name}
        </option>
      ))}
    </Select>
  );
}
